import {
    Calendar,
    ChartColumn,
    CircleHelp,
    FileText,
    Folder,
    GitCommitHorizontal,
    MessageSquare,
    PenLine,
    Pin,
    Table,
    Target,
    type LucideIcon,
} from 'lucide-react';
import type { ColumnaFuncion } from '@/services/asistenteService';

export interface FichaFuncionE1 {
    readonly nombre: string;
    readonly descripcion: string;
    readonly ejemplo: string;
    readonly icono: LucideIcon;
    readonly columna: ColumnaFuncion;
    // Decisión del propietario (v1.8, sobre E1-18): cada tarjeta lleva su propio fondo de token
    // para reconocer la función de un vistazo. Las funciones emparentadas comparten matiz a dos
    // intensidades (documentos, cronología, notas, preguntas) porque la rampa categórica tiene
    // pocos matices a propósito (globals.css absorbe los vecinos en vez de añadirlos). El texto
    // encima es tinta / on-surface-variant: AA en los dos temas (panelFuncionesSinScroll).
    readonly paleta: string;
}

// E1-29: tile content is static configuration keyed by the registry id, so the grid never
// waits on the network for its texts; only availability comes from the server (D-20).
// Texts copied verbatim from E1-15.
export const CATALOGO_FUNCIONES_E1: Readonly<Record<string, FichaFuncionE1>> = Object.freeze({
    documentos_por_secciones: {
        nombre: 'Documentos por secciones',
        descripcion: 'Historia clínica, informes de seguimiento y de evaluación, resumen de la conversación.',
        ejemplo: 'Prepara el informe de seguimiento',
        icono: FileText,
        columna: 'redactar',
        paleta: 'bg-tint-accent-strong',
    },
    plantillas_clinicas: {
        nombre: 'Plantillas clínicas',
        descripcion: 'Análisis funcional, Beck, Young, SOAP, BIRP, DAP e historia clínica.',
        ejemplo: 'Hazme el análisis funcional',
        icono: Table,
        columna: 'redactar',
        paleta: 'bg-tint-accent',
    },
    manuscritos: {
        nombre: 'Manuscritos',
        descripcion: 'Transcribe notas a mano y revisa cuestionarios escaneados.',
        ejemplo: 'Transcribe estas notas',
        icono: PenLine,
        columna: 'redactar',
        paleta: 'bg-tint-brand-highlight',
    },
    cronologia: {
        nombre: 'Cronología del caso',
        descripcion: 'Sesiones y documentos en una línea temporal; marca fechas dudosas.',
        ejemplo: 'Ordena todo por fecha',
        icono: GitCommitHorizontal,
        columna: 'organizar',
        paleta: 'bg-cat-blue-strong',
    },
    carpetas: {
        nombre: 'Carpetas',
        descripcion: 'Agrupa sesiones, notas, documentos y tests por fecha o a tu manera.',
        ejemplo: 'Mete los tests de marzo en una carpeta',
        icono: Folder,
        columna: 'organizar',
        paleta: 'bg-cat-orange-strong',
    },
    fecha_real: {
        nombre: 'Fecha real de documentos',
        descripcion: 'Usa la fecha escrita en el documento; si no hay, la del archivo. Siempre puedes cambiarla.',
        ejemplo: 'Revisa las fechas de los tests',
        icono: Calendar,
        columna: 'organizar',
        paleta: 'bg-cat-blue',
    },
    plan_terapeutico: {
        nombre: 'Plan terapéutico',
        descripcion: 'Propone cambios y avances tras cada sesión; tú apruebas o rechazas.',
        ejemplo: '¿Qué objetivos han avanzado?',
        icono: Target,
        columna: 'analizar',
        paleta: 'bg-cat-green-strong',
    },
    psicometria: {
        nombre: 'Psicometría',
        descripcion: 'Interpreta escalas ya puntuadas: perfil, percentiles y discrepancias.',
        ejemplo: 'Interpreta el GAD-7 y el BDI-II',
        icono: ChartColumn,
        columna: 'analizar',
        paleta: 'bg-cat-indigo-strong',
    },
    preguntar_historial: {
        nombre: 'Preguntar al historial',
        descripcion: 'Cualquier pregunta sobre lo que ha pasado en terapia, con citas.',
        ejemplo: '¿Cuándo habló por primera vez del insomnio?',
        icono: MessageSquare,
        columna: 'analizar',
        paleta: 'bg-cat-purple-strong',
    },
    notas_fijas: {
        nombre: 'Notas fijas y correcciones',
        descripcion: 'Corrige al asistente y la corrección queda fijada para siempre.',
        ejemplo: 'Marcos es su hermano, no su pareja',
        icono: Pin,
        columna: 'para_ti',
        paleta: 'bg-tint-brand-highlight-strong',
    },
    ayuda: {
        nombre: 'Ayuda de TherapyRecap',
        descripcion: 'Dudas de uso de la aplicación, con enlaces directos.',
        ejemplo: '¿Cómo subo notas antiguas?',
        icono: CircleHelp,
        columna: 'para_ti',
        paleta: 'bg-cat-purple',
    },
});

export function fichaFuncion(id: string): FichaFuncionE1 | null {
    return Object.hasOwn(CATALOGO_FUNCIONES_E1, id) ? CATALOGO_FUNCIONES_E1[id] : null;
}
